<div class="services-zones" data-testid="services-zones-page">
  <div class="page-header">
    <h1 class="page-header__title">Services &amp; Zones</h1>
    <p class="page-header__subtitle">
      Configure your service capabilities and the coverage zones where you offer
      them. You can update these at any time.
    </p>
  </div>

  <div class="sz-intro">
    <p class="sz-intro__text">
      You may be engaged to perform a Sub-Service only. Some Services may require
      you to upload credentials.
    </p>
  </div>

  <!-- Section 1 — Service Capabilities -->
  <section class="card sz-section" data-testid="services-zones-capabilities">
    <div class="sz-section__header">
      <h2 class="sz-section__title">1. Service Capabilities</h2>
      <div class="sz-section__count" data-testid="services-zones-capabilities-count">
        {{ offeredServiceCount }} of {{ totalServiceCount }} Services Selected
      </div>
    </div>
    <p class="sz-section__hint">
      Select the desired service capabilities from the list provided. Some
      capabilities include additional sub-services, which can be viewed by
      clicking the expansion icon next to the corresponding item.
    </p>
    <service-capability-picker
      class="capabilities-picker"
      [services]="serviceTypes"
      [showSelectAll]="false"
      (selectionChange)="onServiceCapabilitiesChange($event)"
    ></service-capability-picker>
  </section>

  <!-- Section 2 — Coverage Zones -->
  <section class="card sz-section" data-testid="services-zones-coverage">
    <div class="sz-section__header">
      <h2 class="sz-section__title">2. Coverage Zones</h2>
      <ng-container *ngIf="user.selectedServiceTypes?.length > 0">
        <button
          type="button"
          class="btn btn--primary sz-add-zone"
          data-testid="services-zones-add-zone"
          (click)="addNewServiceZone()"
        >
          <span class="material-icons">add</span>
          Add a New Zone
        </button>
      </ng-container>
    </div>
    <p class="sz-section__hint">
      Once you've selected your capabilities, specify your preferred service zones
      and assign the relevant capabilities to each zone.
    </p>

    <!--
      Unified Coverage Zone form — Add and Edit share ONE template. The Add vs
      Edit difference is driven by `isEditZoneForm` through the form* accessors,
      so saveNewServiceZone / saveUpdatedServiceZone keep reading their original
      working-copy variables and emit a byte-identical payload. The markup is
      defined ONCE as #zoneFormTpl so it can render in two positions without
      duplication: at the TOP for Add, or in place of a zone's read card for Edit.
    -->
    <ng-template #zoneFormTpl>
      <div class="zone-form" data-testid="services-zones-zone-form">
        <div class="zone-form__header">
          <div class="zone-form__title">{{ zoneFormTitle }}</div>
          <div class="zone-form__actions">
            <button
              type="button"
              class="btn-save"
              data-testid="services-zones-zone-form-save"
              (click)="saveZoneForm()"
            >
              <span class="material-icons">save</span> Save
            </button>
            <button
              type="button"
              class="btn-cancel"
              data-testid="services-zones-zone-form-cancel"
              (click)="cancelZoneForm()"
            >
              <span class="material-icons cancel-details">close</span>
              Cancel
            </button>
          </div>
        </div>
        <div class="zone-form__body">
          <div class="zone-form__rail">
            <div class="form-control">
              <label class="form-label">State<span class="req">*</span></label>
              <br />
              <select
                class="form-select"
                data-testid="services-zones-zone-form-state"
                [(ngModel)]="formState"
                (ngModelChange)="handleFormStateChange()"
              >
                <option [ngValue]="''" disabled>Select a state</option>
                <option
                  *ngFor="
                    let serviceZoneStateCountyDetail of serviceZoneStateCountyDetails
                  "
                  [ngValue]="serviceZoneStateCountyDetail"
                >
                  {{ serviceZoneStateCountyDetail.name }}
                </option>
              </select>
            </div>
            <div
              *ngIf="formState?.state !== 'NATIONWIDE'"
              class="form-control"
            >
              <label class="form-label">County<span class="req">*</span></label>
              <br />
              <mat-select
                multiple
                [(value)]="formCounties"
                [disabled]="!formState?.state"
                class="form-multi-select"
                panelClass="form-multi-select-panel"
                data-testid="services-zones-zone-form-county"
              >
                <mat-select-trigger>
                  {{ formCounties[0] || "" }}
                  @if ((formCounties?.length || 0) > 1) {
                  <span class="example-additional-selection">
                    (+{{ (formCounties?.length || 0) - 1 }}
                    {{ formCounties?.length === 2 ? "other" : "others" }})
                  </span>
                  }
                </mat-select-trigger>
                <mat-option
                  *ngFor="let serviceZoneCounty of formCountyOptions"
                  [value]="serviceZoneCounty"
                  class="form-multi-select-option"
                  [disabled]="isOptionDisabled(formCounties, serviceZoneCounty)"
                >
                  {{ serviceZoneCounty }}
                </mat-option>
              </mat-select>
            </div>
          </div>
          <div class="zone-form__picker">
            <div class="txt-heading">Services</div>
            <service-capability-picker
              [services]="formServices"
              [preselectAll]="!isEditZoneForm"
            ></service-capability-picker>
          </div>
        </div>
      </div>
    </ng-template>

    <!--
      Add: the unified form renders at the TOP of the zones section. Edit does NOT
      render here — it renders in place of the edited zone's read card inside the
      loop below, so the two are mutually exclusive on screen.
    -->
    <ng-container
      *ngIf="user.selectedServiceTypes?.length > 0 && toggleNewServiceZone"
    >
      <ng-container *ngTemplateOutlet="zoneFormTpl"></ng-container>
    </ng-container>

    <!-- Empty state — no zones configured yet. -->
    <div
      class="empty-state sz-empty"
      *ngIf="!(user?.serviceZones && user.serviceZones.length > 0)"
      data-testid="services-zones-empty"
    >
      <p>No coverage zones yet.</p>
      <p class="sz-empty__hint" *ngIf="user.selectedServiceTypes?.length > 0">
        Add a zone to tell us where you offer your services.
      </p>
      <p class="sz-empty__hint" *ngIf="!(user.selectedServiceTypes?.length > 0)">
        Select at least one service capability above to start adding zones.
      </p>
    </div>

    <!--
      Coverage Zones read view — one card per zone. The header's top line pins the
      state (or Nationwide) + document-status chip on the left and Edit/Remove on
      the right; the county coverage then wraps on its own row below. The
      document-status chip is three-way (red > amber > green), driven by the
      shared zone-doc.util. The body lists each enabled service as a chip with its
      enabled specialties as sub-chips; a red dot marks each outstanding required
      node so the dot count equals the zone's required count.
    -->
    <ng-container *ngFor="let userServiceZone of user?.serviceZones">
      <ng-container
        *ngIf="
          user.selectedServiceTypes?.length > 0 &&
          editZoneToggle &&
          userServiceZone === editingZone;
          else zoneReadCard
        "
      >
        <ng-container *ngTemplateOutlet="zoneFormTpl"></ng-container>
      </ng-container>
      <ng-template #zoneReadCard>
        <div
          class="zone-card"
          [attr.data-testid]="'services-zones-zone-' + zoneTestKey(userServiceZone)"
        >
          <div class="zone-card__header">
            <div class="zone-card__topline">
              <div class="zone-card__heading">
                <span class="zone-card__state">
                  {{
                    userServiceZone.nationwide
                      ? "Nationwide"
                      : decodeState(userServiceZone.state)
                  }}
                </span>
                <!--
                  Four states, not three. `--clear` means "requirements met", so
                  it requires the zone to HAVE requirements; `--none` is the
                  absence of any. Before they were split, a zone with nothing to
                  satisfy got the green verified treatment, claiming a
                  verification that never happened.
                -->
                <span
                  class="zone-status"
                  [class.zone-status--required]="
                    zoneRequiredCount(userServiceZone) > 0
                  "
                  [class.zone-status--review]="
                    zoneRequiredCount(userServiceZone) === 0 &&
                    zoneUnderReviewCount(userServiceZone) > 0
                  "
                  [class.zone-status--clear]="
                    zoneRequiredCount(userServiceZone) === 0 &&
                    zoneUnderReviewCount(userServiceZone) === 0 &&
                    zoneHasDocNodes(userServiceZone)
                  "
                  [class.zone-status--none]="
                    zoneRequiredCount(userServiceZone) === 0 &&
                    zoneUnderReviewCount(userServiceZone) === 0 &&
                    !zoneHasDocNodes(userServiceZone)
                  "
                >
                  <span class="material-icons zone-status__icon">
                    {{
                      zoneRequiredCount(userServiceZone) > 0
                        ? "error_outline"
                        : zoneUnderReviewCount(userServiceZone) > 0
                          ? "hourglass_empty"
                          : zoneHasDocNodes(userServiceZone)
                            ? "verified"
                            : "remove_circle_outline"
                    }}
                  </span>
                  <ng-container
                    *ngIf="
                      zoneRequiredCount(userServiceZone) > 0;
                      else zoneStatusReviewOrClear
                    "
                  >
                    {{ zoneRequiredLabel(userServiceZone) }}
                  </ng-container>
                  <ng-template #zoneStatusReviewOrClear>
                    <ng-container
                      *ngIf="
                        zoneUnderReviewCount(userServiceZone) > 0;
                        else zoneStatusClear
                      "
                    >
                      {{ zoneUnderReviewLabel(userServiceZone) }}
                    </ng-container>
                  </ng-template>
                  <!--
                    A cleared zone is cleared in one of TWO ways, and they are not
                    the same fact: its requirements are all satisfied, or it has
                    no requirements to satisfy. Both reach here with a required
                    count and an under-review count of 0, so splitting them is
                    what stops a zone with no license requirements claiming that
                    licenses are on file for it.
                  -->
                  <ng-template #zoneStatusClear>
                    <ng-container
                      *ngIf="
                        zoneHasDocNodes(userServiceZone);
                        else noLicensesRequired
                      "
                    >
                      All licenses on file
                    </ng-container>
                  </ng-template>
                  <ng-template #noLicensesRequired
                    >No licenses required</ng-template
                  >
                </span>
              </div>
              <div class="zone-card__actions">
                <ng-container *ngIf="!editZoneToggle">
                  <button
                    type="button"
                    class="zone-action zone-action--edit"
                    [attr.data-testid]="
                      'services-zones-zone-' +
                      zoneTestKey(userServiceZone) +
                      '-edit'
                    "
                    (click)="editServiceZone(userServiceZone)"
                  >
                    <span class="material-icons">edit</span>
                    Edit
                  </button>
                  <button
                    type="button"
                    class="zone-action zone-action--remove"
                    [attr.data-testid]="
                      'services-zones-zone-' +
                      zoneTestKey(userServiceZone) +
                      '-remove'
                    "
                    (click)="removeServiceZone(userServiceZone)"
                  >
                    <span class="material-icons">delete</span>
                    Remove
                  </button>
                </ng-container>
              </div>
            </div>
            <div
              class="zone-card__counties"
              *ngIf="!userServiceZone.nationwide"
            >
              <ng-container
                *ngIf="
                  isAllCounties(userServiceZone.counties);
                  else specificCounties
                "
              >
                <span class="zone-pill">
                  {{ formatCounty(userServiceZone.counties) }}
                </span>
              </ng-container>
              <ng-template #specificCounties>
                <span
                  class="zone-county-chip"
                  *ngFor="let county of userServiceZone.counties"
                >
                  {{ county }}
                </span>
              </ng-template>
            </div>
          </div>
          <div class="zone-card__body">
            <div class="txt-heading">Services &amp; Specialties</div>
            <div class="zone-services-grid">
              <div
                *ngFor="
                  let userService of filterServiceList(userServiceZone.services)
                "
                class="zone-service-chip"
              >
                <div class="zone-service-chip__head">
                  <span
                    class="material-icons zone-service-chip__dot"
                    *ngIf="
                      serviceLevelIsOutstanding(userServiceZone, userService)
                    "
                    title="Documents required"
                    >description</span
                  >
                  <span class="zone-service-chip__name">{{
                    userService.displayName
                  }}</span>
                </div>
                <div
                  class="zone-service-chip__subs"
                  *ngIf="enabledSubTypes(userService).length > 0"
                >
                  <span
                    class="zone-sub-chip"
                    *ngFor="let subType of enabledSubTypes(userService)"
                  >
                    <span
                      class="material-icons zone-sub-chip__dot"
                      *ngIf="
                        subServiceIsOutstanding(
                          userServiceZone,
                          userService,
                          subType
                        )
                      "
                      title="Documents required"
                      >description</span
                    >
                    {{ subType.displayName }}
                  </span>
                </div>
                <!-- Per-service "Add License" — opens the shared upload dialog in
                     place, TAGGED to the outstanding requirement. Shows only while
                     the card has an outstanding required license. A single
                     outstanding requirement opens straight to its upload; more
                     than one offers a menu naming each requirement. -->
                <ng-container
                  *ngIf="
                    serviceCardHasOutstandingLicense(
                      userServiceZone,
                      userService
                    )
                  "
                >
                  <ng-container
                    *ngIf="
                      outstandingLicensesForServiceCard(
                        userServiceZone,
                        userService
                      ) as outstandingLicenses
                    "
                  >
                    <button
                      type="button"
                      class="zone-service-chip__add-license"
                      *ngIf="outstandingLicenses.length === 1"
                      [attr.data-testid]="
                        'services-zones-zone-' +
                        zoneTestKey(userServiceZone) +
                        '-service-' +
                        serviceTestKey(userService) +
                        '-add-license'
                      "
                      (click)="
                        openZoneDocUpload(
                          userServiceZone,
                          userService,
                          null,
                          outstandingLicenses[0]
                        )
                      "
                    >
                      <span class="material-icons">upload_file</span>
                      Add License
                    </button>
                    <ng-container *ngIf="outstandingLicenses.length > 1">
                      <button
                        type="button"
                        class="zone-service-chip__add-license"
                        [matMenuTriggerFor]="addLicenseMenu"
                        [attr.data-testid]="
                          'services-zones-zone-' +
                          zoneTestKey(userServiceZone) +
                          '-service-' +
                          serviceTestKey(userService) +
                          '-add-license'
                        "
                      >
                        <span class="material-icons">upload_file</span>
                        Add License
                      </button>
                      <mat-menu #addLicenseMenu="matMenu">
                        <button
                          mat-menu-item
                          *ngFor="let outstandingLicense of outstandingLicenses"
                          [attr.data-testid]="
                            'services-zones-zone-' +
                            zoneTestKey(userServiceZone) +
                            '-service-' +
                            serviceTestKey(userService) +
                            '-add-license-' +
                            licenseTestKey(outstandingLicense)
                          "
                          (click)="
                            openZoneDocUpload(
                              userServiceZone,
                              userService,
                              null,
                              outstandingLicense
                            )
                          "
                        >
                          {{ outstandingLicense.documentTitle }}
                        </button>
                      </mat-menu>
                    </ng-container>
                  </ng-container>
                </ng-container>
              </div>
            </div>
          </div>
        </div>
      </ng-template>
    </ng-container>
  </section>
</div>

<!-- Shared single-file zone-document upload dialog (drop zone + inline preview +
     coverage form). The host owns the per-node context; the dialog owns the
     file/preview UI and emits the file + coverage on save. -->
<zone-doc-upload-dialog
  *ngIf="zoneDocUploadOpen"
  [zoneLabel]="
    zoneDocUploadZone?.nationwide
      ? 'Nationwide'
      : decodeState(zoneDocUploadZone?.state)
  "
  [service]="zoneDocUploadService"
  [scopedSubType]="zoneDocUploadSubType"
  [siblings]="zoneDocUploadSiblings"
  [license]="zoneDocUploadLicense"
  [hasServiceLevelNode]="zoneDocUploadHasServiceLevelNode"
  [submitting]="zoneDocUploadSubmitting"
  (save)="saveZoneDocUpload($event)"
  (cancelled)="cancelZoneDocUpload()"
></zone-doc-upload-dialog>
